import { graphql } from '@/__generated__';
import { GenericMaterializedFilter } from '@/__generated__/graphql';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';

export const GetSavedQueriesByEnvironment = graphql(/* GraphQL */ `
  query SavedQueries(
    $first: Int!
    $after: String
    $environmentId: BigInt!
    $userId: BigInt
    $filter: GenericMaterializedFilter
  ) {
    environment(id: $environmentId, userId: $userId) {
      definition {
        lastUpdatedAt
        savedQueries(first: $first, after: $after, filter: $filter) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              name
              uniqueId
              description
              queryParams {
                groupBy
                limit
                metrics
                orderBy
                where {
                  whereFilters {
                    whereSqlTemplate
                  }
                }
              }
              exports {
                name
                config {
                  alias
                  exportAs
                  schema
                  database
                }
              }
            }
          }
        }
      }
    }
  }
`);

type SavedQueryByEnvironmentParams = {
  first: number;
  after?: string;
  environmentId: number | undefined;
  userId?: number | 'loading' | null;
  filter?: GenericMaterializedFilter;
  pause?: boolean;
};

/**
 * SavedQueries are Definition Only
 */
export const useDiscoveryDefinitionSavedQueries = ({
  first,
  after,
  environmentId,
  userId,
  filter,
  pause,
}: SavedQueryByEnvironmentParams) => {
  const query = usePaginatedQuery(
    {
      query: GetSavedQueriesByEnvironment,
      variables: {
        first,
        after,
        environmentId,
        userId: userId as number | null,
        filter,
      },
      pause: pause || !environmentId || !first || userId === 'loading',
    },
    (data) => {
      const saved_queries = data.environment.definition?.savedQueries;
      const pageInfo = saved_queries?.pageInfo;
      if (saved_queries == null || pageInfo == null) return undefined;
      return {
        totalCount: saved_queries.totalCount ?? null,
        endCursor: pageInfo.endCursor ?? null,
        hasNextPage: pageInfo.hasNextPage,
      };
    },
    (data) => {
      return data.environment.definition?.savedQueries.edges.map((edge) => edge.node);
    },
    (data) => data.environment.definition?.lastUpdatedAt,
  );

  useCacheMonitor({ definition: query.lastUpdatedAt });

  return query;
};
